/**
 * Profile (`/account/profile`, web-account-pages D5, profile-web.html Mockup 10
 * in the prototype's centered 720px column): who the client is, My info edited
 * where it stands and confirmed with "Your info is saved.", saved cards with
 * Remove, the time zone in words as set by the device, and Privacy · Terms.
 * Profile lists no forms; waiting forms live on the home (Gabriel 2026-10-22).
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'profile',
  description:
    "The client's Profile: My info edited in place, saved cards with Remove, the time zone set by the device, and the legal pages.",
  options: {},
  parts: {
    page: { description: 'The whole page.', required: false },
    identity: { description: "The client's initials or photo, name, @username, member since, and sessions.", required: false },
    myInfo: { description: 'My info: first name, last name, email and phone.', required: true },
    infoRow: { description: 'One My info row: a click starts editing with that field.', required: true },
    infoField: { description: 'One My info field while editing.', required: true, states: ['active'] },
    fieldError: { description: "A field's problem (an email that is not one).", required: true },
    save: { description: 'Save changes: enabled when something changed and every field is valid.', required: true, states: ['disabled'], role: 'primary-action' },
    cancelEdit: { description: 'Cancel: leaves the saved values.', required: false, role: 'secondary-action' },
    saveError: { description: 'The save did not go through.', required: true },
    saved: { description: 'Your info is saved.', required: false },
    cards: { description: 'Payment methods: the saved cards.', required: false },
    card: { description: 'One saved card with Remove.', required: false },
    removeCard: { description: 'Remove: opens the remove window.', required: false },
    removeWindow: { description: 'The remove window: Keep card and Remove card.', required: true },
    timezone: { description: 'The time zone in words, set by this device.', required: false },
    legal: { description: 'Privacy · Terms, Powered by Telify and the version.', required: true },
  },
  actions: {
    startEdit: { description: 'Turns the rows into fields, with the clicked one focused.', args: [{ name: 'field', kind: 'item', list: 'info', key: 'key' }] },
    setField: { description: 'Types into a field.', args: [
        { name: 'field', kind: 'item', list: 'info', key: 'key' },
        { name: 'value', kind: 'value', type: 'string', format: 'text' },
      ] },
    focusField: { description: 'Marks the field being typed in.', args: [{ name: 'field', kind: 'item', list: 'info', key: 'key' }] },
    cancelEdit: { description: 'Leaves the saved values.', args: [] },
    save: { description: 'Saves the four fields together.', args: [] },
    askRemoveCard: { description: 'Opens the remove window for a card.', args: [{ name: 'id', kind: 'item', list: 'cards', key: 'id' }] },
    keepCard: { description: 'Closes the remove window.', args: [] },
    removeCard: { description: 'Removes the card.', args: [] },
  },
  events: {
    saved: 'My info was saved.',
    cardRemoved: 'A saved card was removed.',
  },
});

/** A My info field's name. */
export type ProfileField = 'firstName' | 'lastName' | 'email' | 'phone';

/** One My info row. */
export interface ProfileInfoRow {
  readonly key: ProfileField;
  /** First name, Last name, Email, Phone. */
  readonly label: string;
  /** The saved value, or null when not set. */
  readonly value: string | null;
}

/** My info while editing. */
export interface ProfileEdit {
  /** What is typed in each field. */
  readonly values: Readonly<Record<ProfileField, string>>;
  /** The field being typed in. */
  readonly active: ProfileField;
  /** The email's problem, or null. */
  readonly emailError: string | null;
  /** Each field's length cap. */
  readonly maxLength: Readonly<Record<ProfileField, number>>;
  /** True when something changed and every field is valid. */
  readonly canSave: boolean;
  /** True while the save is on its way. */
  readonly saving: boolean;
  /** True when the last save did not go through. */
  readonly failed: boolean;
}

/** One saved card. */
export interface ProfileCard {
  readonly id: string;
  /** Visa, Mastercard, Amex. */
  readonly brand: string;
  /** visa, mastercard, or other: which badge is drawn. */
  readonly badge: 'visa' | 'mastercard' | 'other';
  /** Null for a Link payment method, which reads Link and has no expiry either (Stripe's digits for it are made up). */
  readonly last4: string | null;
  /** "Expires 08/28". */
  readonly expires: string | null;
}

/** What Profile knows. */
export interface ProfileState {
  /** loading until the profile is read; error when it could not be. */
  readonly status: 'loading' | 'ready' | 'error';
  /** The client's name. */
  readonly name: string;
  /** Their initials, drawn when there is no photo. */
  readonly initials: string;
  /** Their photo, or null. */
  readonly avatarUrl: string | null;
  /** "@mayareyes · Member since Aug 2026". */
  readonly meta: string;
  /** How many sessions they completed. */
  readonly sessions: number;
  /** My info's rows. */
  readonly info: readonly ProfileInfoRow[];
  /** My info while editing, or null. */
  readonly edit: ProfileEdit | null;
  /** True for a moment after a save: "Your info is saved." */
  readonly saved: boolean;
  /** The saved cards: loading, ready, or unavailable when the card service is down. */
  readonly cardsStatus: 'loading' | 'ready' | 'unavailable';
  readonly cards: readonly ProfileCard[];
  /** The remove window while open, or null. */
  readonly removing: { readonly card: string; readonly busy: boolean; readonly failed: boolean } | null;
  /** "Eastern Time (EDT)". */
  readonly timezone: string;
  /** The legal pages. */
  readonly privacyHref: string;
  readonly termsHref: string;
  /** "v1.4.0". */
  readonly version: string;
}

/** What a drawing can ask Profile to do. */
export interface ProfileActions {
  /** Start editing My info with this field focused. */
  readonly startEdit: (field: ProfileField) => void;
  /** Type into a field. */
  readonly setField: (field: ProfileField, value: string) => void;
  /** Mark the field being typed in. */
  readonly focusField: (field: ProfileField) => void;
  /** Leave the saved values. */
  readonly cancelEdit: () => void;
  /** Save the four fields. */
  readonly save: () => void;
  /** Open the remove window for a card. */
  readonly askRemoveCard: (id: string) => void;
  /** Close the remove window. */
  readonly keepCard: () => void;
  /** Remove the card in the remove window. */
  readonly removeCard: () => void;
}

/** What Profile tells its host. */
export interface ProfileEvents {
  /** My info was saved. */
  readonly saved: Record<string, never>;
  /** A saved card was removed. */
  readonly cardRemoved: Record<string, never>;
}
